/**
 * Hex parsing and WCAG contrast maths.
 *
 * Used by the identity theme so a client-picked colour can be checked before
 * it is applied app-wide: a hue that fails contrast against white does not
 * announce itself, it just makes every primary button in the product slightly
 * harder to read for everyone. Pure functions, no DOM.
 */

/** True for #RGB or #RRGGBB (case-insensitive, leading # required). */
export const isValidHex = (value) => /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value ?? '').trim())

/**
 * Normalises to lowercase `#rrggbb`, accepting `#RGB` / `RRGGBB` with or
 * without the hash. Returns '' for anything unparseable, so a caller can treat
 * '' uniformly as "no colour, use the platform default".
 */
export function normalizeHex(value) {
  let hex = String(value ?? '').trim().toLowerCase()
  if (hex === '') return ''
  if (!hex.startsWith('#')) hex = `#${hex}`
  if (/^#[0-9a-f]{3}$/.test(hex)) hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : ''
}

/** `#rrggbb` → `{ r, g, b }` in 0–255, or null when unparseable. */
export function hexToRgb(value) {
  const hex = normalizeHex(value)
  if (!hex) return null
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  }
}

const toHex = (channel) => Math.round(Math.min(255, Math.max(0, channel))).toString(16).padStart(2, '0')

/** Mixes `a` toward `b`; `weight` is how much of `b` lands in the result. */
export function mixHex(a, b, weight) {
  const from = hexToRgb(a)
  const to = hexToRgb(b)
  if (!from || !to) return normalizeHex(a)
  const at = (key) => from[key] + (to[key] - from[key]) * weight
  return `#${toHex(at('r'))}${toHex(at('g'))}${toHex(at('b'))}`
}

/**
 * `#rrggbb` → `{ h, s, l }` with h in 0–360 and s/l in 0–100, or null.
 *
 * HSL is what a "keep the hue, change how dark it is" transform needs: mixing
 * a colour toward black in sRGB drags its saturation down with it, which is how
 * a brand colour turns into grey the moment you darken it enough to sit under
 * white text.
 */
export function hexToHsl(value) {
  const rgb = hexToRgb(value)
  if (!rgb) return null
  const r = rgb.r / 255
  const g = rgb.g / 255
  const b = rgb.b / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  const l = (max + min) / 2

  if (delta === 0) return { h: 0, s: 0, l: l * 100 }

  const s = delta / (1 - Math.abs(2 * l - 1))
  let h
  if (max === r) h = ((g - b) / delta) % 6
  else if (max === g) h = (b - r) / delta + 2
  else h = (r - g) / delta + 4

  return { h: ((h * 60) % 360 + 360) % 360, s: s * 100, l: l * 100 }
}

/** `{ h, s, l }` (0–360 / 0–100 / 0–100) → `#rrggbb`. */
export function hslToHex({ h, s, l }) {
  const sat = s / 100
  const lig = l / 100
  const c = (1 - Math.abs(2 * lig - 1)) * sat
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = lig - c / 2
  const [r, g, b] =
    h < 60 ? [c, x, 0]
    : h < 120 ? [x, c, 0]
    : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c]
    : h < 300 ? [x, 0, c]
    : [c, 0, x]
  return `#${toHex((r + m) * 255)}${toHex((g + m) * 255)}${toHex((b + m) * 255)}`
}

/** WCAG 2.x relative luminance (0 = black, 1 = white), or null when invalid. */
export function relativeLuminance(value) {
  const rgb = hexToRgb(value)
  if (!rgb) return null
  const channel = (c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

/**
 * Contrast ratio between two hex colours, 1–21, or null when either is
 * invalid. AA wants 4.5 for normal text and 3 for large text and UI graphics.
 */
export function contrastRatio(a, b) {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  if (la == null || lb == null) return null
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}
